import { MobileDatePicker, DesktopDatePicker } from "@mui/x-date-pickers";
import { TextFieldProps, IconButton, InputAdornment, Box } from "@mui/material";
import { useDeviceType } from "@hooks/useDeviceType";
import CalendarIcon from "@static/icons/calendar.svg";
import { MobileDatePickerProps } from "@mui/x-date-pickers/MobileDatePicker";
import { DesktopDatePickerProps } from "@mui/x-date-pickers/DesktopDatePicker";
import { ReactElement, useRef, useCallback, useState } from "react";
import type { FocusEvent } from "react";
import { isValid, parse } from "date-fns";
import { useResponsiveDatePickerStyle } from "./style";

type ResponsiveDatePickerProps<TInputDate = Date | null, TDate = TInputDate> = (
    | MobileDatePickerProps<TInputDate, TDate>
    | DesktopDatePickerProps<TInputDate, TDate>
) & {
    hideCalendarIcon?: boolean;
    renderInput(props: TextFieldProps): ReactElement;
};

export const ResponsiveDatePicker = <TInputDate = Date | null, TDate = TInputDate>(
    props: ResponsiveDatePickerProps<TInputDate, TDate>
) => {
    const { isMobileOrTablet } = useDeviceType();
    const sxStyle = useResponsiveDatePickerStyle();
    const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null);
    const inputRef = useRef<HTMLDivElement | null>(null);

    const { renderInput, onChange, inputFormat, hideCalendarIcon = false, ...restProps } = props;

    type OnChangeFn = NonNullable<typeof onChange>;
    type OnChangeContext = Parameters<OnChangeFn>[1];

    const safeOnChange = useCallback(
        (value: TDate, context: OnChangeContext) => {
            if (value instanceof Date && !isValid(value)) {
                return;
            }

            onChange?.(value, context);
        },
        [onChange]
    );

    const blurContext = (undefined as unknown) as OnChangeContext;

    const customRenderInput = useCallback(
        (textFieldProps: TextFieldProps): ReactElement => {
            const { InputProps, ...otherTextFieldProps } = textFieldProps;

            const originalOnBlur = otherTextFieldProps.onBlur;
            const wrappedOnBlur = (event: FocusEvent<HTMLInputElement>) => {
                const rawValue = event.currentTarget.value;

                originalOnBlur?.(event);

                if (!rawValue) {
                    safeOnChange((null as unknown) as TDate, blurContext);

                    return;
                }

                if (typeof inputFormat !== "string") {
                    return;
                }

                const parsedDate = parse(rawValue, inputFormat, new Date());
                if (!isValid(parsedDate)) {
                    safeOnChange((null as unknown) as TDate, blurContext);

                    return;
                }
            };

            let endAdornment: ReactElement | undefined;

            if (!hideCalendarIcon) {
                // Extract the onClick handler from the original endAdornment
                // The DatePicker provides an IconButton in endAdornment with the click handler
                const endAdornmentElement = InputProps?.endAdornment as ReactElement | undefined;
                const children = endAdornmentElement?.props?.children;
                const childElement =
                    children && typeof children === "object" && "props" in children
                        ? (children as ReactElement)
                        : undefined;

                const onClickHandler = endAdornmentElement?.props?.onClick ?? childElement?.props?.onClick;

                // Replace the endAdornment with our custom calendar icon, preserving the onClick handler
                endAdornment = (
                    <InputAdornment position="end">
                        <IconButton
                            aria-label="open date picker"
                            edge="end"
                            sx={sxStyle.button}
                            onClick={onClickHandler}
                        >
                            <Box sx={sxStyle.iconBox}>
                                <CalendarIcon />
                            </Box>
                        </IconButton>
                    </InputAdornment>
                );
            }

            // Use a callback ref to update the anchor element when the TextField is mounted
            const originalRef = otherTextFieldProps.ref;
            const combinedRef = (node: HTMLDivElement | null) => {
                inputRef.current = node;
                setAnchorEl(node);
                // Also call the original ref if it exists and is a function
                if (originalRef && typeof originalRef === "function") {
                    originalRef(node);
                }
            };

            return renderInput({
                ...otherTextFieldProps,
                onBlur: wrappedOnBlur,
                InputProps: {
                    ...InputProps,
                    endAdornment,
                    placeholder: "DD/MM/YYYY",
                },
                ref: combinedRef,
            });
        },
        [renderInput, sxStyle, hideCalendarIcon]
    );

    const commonProps = {
        ...restProps,
        inputFormat,
        renderInput: customRenderInput,
        onChange: safeOnChange,
    };

    if (isMobileOrTablet) {
        // The DatePicker expects props spread and this component is generic by design.
        // eslint-disable-next-line react/jsx-props-no-spreading
        return <MobileDatePicker {...(commonProps as MobileDatePickerProps<TInputDate, TDate>)} />;
    }

    const existingPopperProps = (commonProps as DesktopDatePickerProps<TInputDate, TDate>).PopperProps ?? {};
    const desktopProps: DesktopDatePickerProps<TInputDate, TDate> = {
        ...(commonProps as DesktopDatePickerProps<TInputDate, TDate>),
        PopperProps: {
            placement: "bottom-start" as const,
            anchorEl,
            sx: sxStyle.calendarPopper,
            ...existingPopperProps,
        },
    };

    // eslint-disable-next-line react/jsx-props-no-spreading
    return <DesktopDatePicker {...desktopProps} />;
}
